import { useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Select, TextField } from '@ankr.com/ui';
import { Box, Button, Checkbox, MenuItem, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import { AbiInput, AbiItem, isAddress } from 'web3-utils';

import { Copy, NumberField } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { useGetAccountAddressQuery } from 'modules/store';

import { useGetGasLimitQuery } from '../../actions/getGasLimit';
import { getValidateInputType } from '../../utils/getValidateInputType';
import { handleChangeInputType } from '../../utils/handleChangeInputType';
import { AbiPanel, OnLoadParams } from '../AbiPanel';
import { Signature } from '../Signature';
import translation from './executeForm.translation';

export interface ExecuteFormSubmitParams {
  taskName: string;
  taskContractAddress: string;
  gasLimit: BigNumber;
  abiItem: AbiItem | null;
  abi: AbiItem[];
  isCompatible: boolean;
  isCustomAbi: boolean;
  params: string[];
  customData: string | null;
}

export interface ExecuteFormValues {
  taskName: string;
  taskContractAddress: string;
  abiName: string;
  params: { value: string }[];
  selectedAbiItems: AbiItem[];
  gasLimit: string;
  isCompatible: boolean;
  isCustomData: boolean;
  isCustomAbi: boolean;
  customData: string | null;
  showCustomAbi: boolean;
}

interface Props {
  onSubmit: (params: ExecuteFormSubmitParams) => void;
  initialFormValues: ExecuteFormValues | null;
  onSaveFormValues: (formState: ExecuteFormValues) => void;
}

const EMPTY_ABI_ITEMS: AbiItem[] = [];
const EMPTY_INPUTS: AbiInput[] = [];
const EMPTY_PARAMS: ExecuteFormValues['params'] = [];
// issue https://ankrnetwork.atlassian.net/browse/AUTO-166
const MIN_GAS_LIMIT = new BigNumber(21000);

export function ExecuteForm({
  onSubmit,
  initialFormValues,
  onSaveFormValues,
}: Props): JSX.Element {
  const { t, keys } = useTranslation(translation);

  const useFormMethods = useForm<ExecuteFormValues>({
    mode: 'onChange',
    defaultValues: initialFormValues ?? {
      taskContractAddress: '',
      params: EMPTY_PARAMS,
      selectedAbiItems: EMPTY_ABI_ITEMS,
      isCompatible: false,
      isCustomData: false,
      isCustomAbi: false,
    },
  });
  const {
    control,
    watch,
    resetField,
    setValue,
    handleSubmit: handleSubmitForm,
    formState: { errors },
  } = useFormMethods;

  const { remove: resetParams } = useFieldArray({
    control,
    name: 'params',
  });

  const isCompatible = watch('isCompatible');
  const isCustomData = watch('isCustomData');
  const selectedAbiItems = watch('selectedAbiItems') ?? EMPTY_ABI_ITEMS;
  const abiName = watch('abiName');
  const taskName = watch('taskName');
  const params = watch('params');
  const gasLimit = watch('gasLimit');
  const customData = watch('customData');
  const taskContractAddress = watch('taskContractAddress');
  const showCustomAbi = watch('showCustomAbi');

  const filteredAbiItems = useMemo(
    () =>
      selectedAbiItems.filter(
        ({ type, payable, stateMutability }) =>
          type === 'function' &&
          !payable &&
          !['view', 'payable'].includes(String(stateMutability)),
      ),
    [selectedAbiItems],
  );

  const selectedAbi = useMemo(
    () =>
      abiName
        ? filteredAbiItems.find(({ name }) => name === abiName)
        : undefined,
    [abiName, filteredAbiItems],
  );

  const inputs: AbiInput[] = selectedAbi?.inputs || EMPTY_INPUTS;

  const filledFirstStep = !!filteredAbiItems.length && !isCompatible;

  const isMainFieldsValid = !!(taskContractAddress && taskName && gasLimit);
  const isCompatibleFieldsValid = isCustomData ? !!customData : true;
  const isFormValid =
    isMainFieldsValid &&
    (isCompatible ? isCompatibleFieldsValid : !!selectedAbi?.name) &&
    !Object.keys(errors).length;

  const handleSubmit = useMemo(
    () =>
      handleSubmitForm(formValues => {
        const {
          taskName,
          taskContractAddress,
          params,
          gasLimit,
          isCompatible,
          isCustomAbi,
          customData,
        } = formValues;
        if (!isCompatible && !selectedAbi) {
          return;
        }

        onSubmit({
          taskName,
          taskContractAddress,
          abi: filteredAbiItems,
          abiItem: selectedAbi ?? null,
          gasLimit: new BigNumber(gasLimit),
          params: params.map(({ value }) => value),
          isCompatible,
          isCustomAbi,
          customData,
        });
        onSaveFormValues(formValues);
      }),
    [
      filteredAbiItems,
      handleSubmitForm,
      onSaveFormValues,
      onSubmit,
      selectedAbi,
    ],
  );

  const resetAbiFieldData = useCallback((): void => {
    resetParams();
    setValue('abiName', '');
    setValue('selectedAbiItems', EMPTY_ABI_ITEMS);
  }, [resetParams, setValue]);

  const onLoad = useCallback(
    ({ abi: abiItems, isCompatible, isCustomAbi }: OnLoadParams) => {
      setValue('isCompatible', isCompatible);
      setValue('isCustomAbi', isCustomAbi);
      resetAbiFieldData();

      setValue('selectedAbiItems', abiItems || EMPTY_ABI_ITEMS);
    },
    [resetAbiFieldData, setValue],
  );

  useEffect(() => {
    resetField('params');
  }, [inputs, resetField]);

  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: estimatedGasLimit } = useGetGasLimitQuery({
    abi: selectedAbiItems,
    currentAccount: accountAddress,
    contractAddress: taskContractAddress,
    functionName: selectedAbi?.name ?? '',
    params: params.map(({ value }) => value),
  });

  useEffect(() => {
    if (!estimatedGasLimit) {
      return;
    }

    setValue('gasLimit', estimatedGasLimit.toString(), {
      shouldValidate: true,
    });
  }, [estimatedGasLimit, setValue]);

  const showGasLimitField = filteredAbiItems.length || isCompatible;

  useEffect(() => {
    if (!showGasLimitField) {
      resetField('gasLimit');
    }
  }, [resetField, showGasLimitField]);

  return (
    <FormProvider {...useFormMethods}>
      <Box
        data-tid="create-task-execute-form"
        component="form"
        display="flex"
        flexDirection="column"
        onSubmit={handleSubmit}
      >
        <Typography variant="button2">{t(keys.taskName)}</Typography>

        <Controller
          name="taskName"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <TextField
              data-tid="create-task-execute-task-name-input"
              value={value}
              onChange={onChange}
              error={!!errors.taskName}
              sx={{ mt: 8 }}
              variant="filled"
              helperText={t(keys.taskNameHelp)}
            />
          )}
        />

        <Typography variant="button2" mt={10}>
          {t(keys.executeTitle)}
        </Typography>

        <Controller<ExecuteFormValues>
          name="taskContractAddress"
          control={control}
          rules={{
            required: true,
            validate: value => isAddress(String(value)),
          }}
          render={({ field: { onChange, value } }) => (
            <TextField
              data-tid="create-task-execute-task-contract-address"
              value={value}
              onChange={(...params) => {
                if (!showCustomAbi) {
                  resetAbiFieldData();
                }

                onChange(...params);
              }}
              error={!!errors.taskContractAddress}
              sx={{ mt: 8, mb: 1 }}
              variant="filled"
              label={t(keys.contractAddressLabel)}
              InputProps={{
                endAdornment: (
                  <Copy
                    text={String(value)}
                    onCopy={async () => {
                      const text = await navigator.clipboard.readText();
                      setValue('taskContractAddress', text, {
                        shouldValidate: true,
                      });
                    }}
                  />
                ),
              }}
            />
          )}
        />

        <AbiPanel onLoad={onLoad} />

        {filledFirstStep && (
          <>
            <Box mt={8}>
              <Controller
                name="abiName"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value: fieldValue } }) => (
                  <Select
                    data-tid="create-task-execute-abi-name-select"
                    value={fieldValue}
                    onChange={event => {
                      onChange(event);
                      resetField('gasLimit');
                    }}
                    disabled={!filteredAbiItems.length}
                    label={t(keys.automizedLabel)}
                    error={!!errors.abiName}
                    variant="filled"
                    renderValue={() => {
                      const abi = filteredAbiItems.find(
                        ({ name }) => fieldValue === name,
                      );
                      if (!abi) {
                        return fieldValue;
                      }

                      return <Signature value={abi} />;
                    }}
                  >
                    {filteredAbiItems.map(abi => (
                      <MenuItem
                        data-tid={`create-task-execute-abi-name-select-item-${
                          abi?.name ?? ''
                        }`}
                        key={abi.name}
                        value={abi.name}
                      >
                        <Signature value={abi} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {inputs.map(({ type, name }, index) => {
              const isIntType = type.toLowerCase().indexOf('int') > -1;
              const isUintType = type.toLowerCase().indexOf('uint') > -1;

              return (
                <Controller
                  key={name}
                  name={`params.${index}.value`}
                  control={control}
                  rules={{
                    required: true,
                    validate: getValidateInputType(type),
                  }}
                  render={({ field: { onChange, value } }) => {
                    return isIntType ? (
                      <NumericFormat
                        data-tid={`create-task-execute-params-input-${index}`}
                        thousandSeparator=","
                        valueIsNumericString
                        allowNegative={isIntType && !isUintType}
                        value={String(value ?? '')}
                        onChange={handleChangeInputType(type, onChange)}
                        customInput={TextField}
                        sx={{ mt: 8 }}
                        variant="filled"
                        error={!!errors?.params?.[index]}
                        helperText={
                          type.indexOf('uint') === 0 ? t(keys.amountInWei) : ''
                        }
                        label={
                          <>
                            <Box
                              component="span"
                              sx={theme => ({
                                color: theme.palette.success.main,
                              })}
                            >
                              {type}
                            </Box>
                            &nbsp;{name}
                          </>
                        }
                      />
                    ) : (
                      <TextField
                        data-tid={`create-task-execute-params-input-${index}`}
                        value={value ?? ''}
                        onChange={handleChangeInputType(type, onChange)}
                        sx={{ mt: 8 }}
                        variant="filled"
                        error={!!errors?.params?.[index]}
                        helperText={
                          type.indexOf('uint') === 0 ? t(keys.amountInWei) : ''
                        }
                        label={
                          <>
                            <Box
                              component="span"
                              sx={theme => ({
                                color: theme.palette.success.main,
                              })}
                            >
                              {type}
                            </Box>
                            &nbsp;{name}
                          </>
                        }
                      />
                    );
                  }}
                />
              );
            })}
          </>
        )}

        {isCompatible && (
          <>
            <Box display="flex" gap={2} alignItems="center" mt={8}>
              <Checkbox
                data-tid="create-task-execute-custom-data-checkbox"
                checked={isCustomData}
                onChange={(_, checked) => setValue('isCustomData', checked)}
              />

              <Typography variant="subtitle3">{t(keys.customData)}</Typography>
            </Box>

            {isCustomData && (
              <Controller
                name="customData"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    data-tid="create-task-execute-custom-data-input"
                    value={value}
                    onChange={onChange}
                    sx={{ mt: 4 }}
                    error={!!errors.customData}
                  />
                )}
              />
            )}
          </>
        )}

        {(filteredAbiItems.length || isCompatible) && (
          <NumberField
            data-tid="create-task-execute-gas-limit-input"
            name="gasLimit"
            min={MIN_GAS_LIMIT}
            control={control}
            sx={{ mt: 8 }}
            error={!!errors.gasLimit}
            variant="filled"
            inputMode="numeric"
            label={t(keys.gasLimitLabel)}
            helperText={t(keys.gasLimitHelp)}
            integerOnly
            required
          />
        )}

        <Button
          data-tid="create-task-execute-submit-button"
          size="large"
          type="submit"
          sx={{ mt: 10 }}
          disabled={!isFormValid}
        >
          {t(keys.submit)}
        </Button>
      </Box>
    </FormProvider>
  );
}
